import React, { createContext, useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom';
import { postRequest } from '../../Back/pages/CustomHttp';
import PareChild from './PareChild';
import $ from "jquery";
import {
  normalizeCustomPracticeFrontJson,
  scheduleFrontJsonCheckboxRestore,
  bumpCheckboxRestoreEpoch,
  SENTENCE_BUILDER_LOADER_DELAY_MS,
  SENTENCE_BUILDER_DEBOUNCE_MS,
  SENTENCE_BUILDER_API_TIMEOUT_MS,
  FORM_SELECTION_SYNC_DEBOUNCE_MS,
  isCustomPracticeAccount,
  resolveCustomPracticeDisplaySessionMinutes,
  clearSentenceBuilderSectionFields,
} from '../../Back/utils/customPractice';

export const changeFuns = createContext();

let tmpAllCheckedArray = [];
let tmpAllAddNewData = {};

function resolveAddNewFieldKey(target) {
    const fieldValue = target?.value || '';
    if (!fieldValue.includes('_addNewArray')) {
        return '';
    }
    try {
        const parsed = JSON.parse(target.id);
        if (typeof parsed?.id === 'string' && parsed.id) {
            return parsed.id;
        }
        if (typeof parsed?.editModeName === 'string' && parsed.editModeName) {
            return parsed.editModeName;
        }
    } catch {
        // ignore invalid JSON ids
    }
    return fieldValue.replace('_addNewArray', '_add-new');
}

function collectFormSelectionFromElements(formElements) {
    const data = {};
    for (let i = 0; i < formElements.length; i += 1) {
        const el = formElements[i];
        const name = el.name?.trim();
        if (!name) {
            continue;
        }
        const { value, id, checked, type } = el;
        if (value.includes('_addNewArray')) {
            if (!checked) {
                continue;
            }
            try {
                const parsed = { ...JSON.parse(id), checked };
                const arrayKey = value;
                if (!data[arrayKey]) {
                    data[arrayKey] = [];
                }
                data[arrayKey].push(parsed);
            } catch {
                // skip invalid add-new ids
            }
            continue;
        }
        if (type === 'radio') {
            if (checked) {
                data[id || name] = true;
            }
            continue;
        }
        if (type === 'select-one') {
            data[name] = value;
            continue;
        }
        if (name.includes('notes') || el.tagName === 'TEXTAREA') {
            data[name] = value;
            continue;
        }
        if (type === 'checkbox') {
            data[name] = checked;
        }
    }
    return data;
}

function resolveLiveCustomPracticeMeta(r) {
    return r.customPracticeMetaRef?.current ?? r.customPracticeMeta ?? {};
}

function resolveSentenceBuilderSessionDuration(liveMeta = {}) {
    if (isCustomPracticeAccount()) {
        const hidden =
            document.querySelector(
                '#notenest-accordion-form input[type="hidden"][name="session_duration"]'
            ) ||
            document.querySelector('input[type="hidden"][name="session_duration"]');
        const hiddenMinutes =
            hidden?.value !== undefined && hidden.value !== ''
                ? hidden.value
                : '';
        return String(
            resolveCustomPracticeDisplaySessionMinutes(liveMeta, hiddenMinutes) ?? ''
        );
    }
    return String($('#session_duration').val() || '');
}

function resolveSentenceBuilderDateOfService(r) {
    return (
        r.dataChildToParent ??
        r.commonData?.commonData?.date_of_service ??
        r.commonData?.date_of_service ??
        undefined
    );
}

function buildSentenceBuilderPostData(newData, r, highlightId = '', highlightData = '') {
    const isCustomPractice = isCustomPracticeAccount();
    const liveMeta = isCustomPractice ? resolveLiveCustomPracticeMeta(r) : {};
    const sessionDuration = resolveSentenceBuilderSessionDuration(liveMeta);
    const dateOfService = resolveSentenceBuilderDateOfService(r);
    return {
        ...newData,
        client_id: r.userId,
        client_document_id: r.viewEdit.client_document_id,
        view_edit_flag: 0,
        notes_id: r.notes_id ?? 0,
        highlightedWordsId: highlightId,
        highlightedWords: highlightData,
        sessionDuration,
        ...(dateOfService != null && dateOfService !== ''
            ? { date_of_service: dateOfService }
            : {}),
        ...(isCustomPractice ? liveMeta : {}),
    };
}

function applySentenceBuilderResponseToParent(r, postdata, isCustomPractice) {
    if (!postdata) {
        return;
    }
    // Parent callbacks merge only safe fields (is_updated, client_document_id).
    r.setViewEdit?.(postdata);
    // Custom practice: never round-trip session/date header fields from sentence-builder API.
    if (isCustomPractice) {
        return;
    }
    if (r.setDateOfServices && postdata.date_of_service) {
        r.setDateOfServices(postdata.date_of_service);
    }
    if (r.setDurations && postdata.session_duration != null) {
        r.setDurations(postdata.session_duration);
    }
}

function isSentenceBuilderApiSuccess(postdata) {
    if (!postdata || postdata.status === false) {
        return false;
    }
    return postdata.paragraph != null;
}

function clearSectionFormFields(form, prefix) {
    if (!prefix) {
        return;
    }
    clearSentenceBuilderSectionFields(prefix, form);
    tmpAllCheckedArray = tmpAllCheckedArray.filter(
        (key) => !key.startsWith(prefix)
    );
    Object.keys(tmpAllAddNewData).forEach((key) => {
        if (key.startsWith(prefix)) {
            delete tmpAllAddNewData[key];
        }
    });
    if (typeof window !== 'undefined') {
        window.dispatchEvent(
            new CustomEvent('sentence-builder-section-reset', {
                detail: { prefix },
            })
        );
    }
}

function syncPayloadToParent(r, postRequestData) {
    const persistedFrontJson = normalizeCustomPracticeFrontJson(postRequestData);
    r.setpayloadsData?.(postRequestData);
    r.onFormSelectionChange?.(persistedFrontJson);
}

function schedulePayloadSyncToParent(r, postRequestData, timerRef) {
    if (timerRef.current) {
        clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
        timerRef.current = null;
        syncPayloadToParent(r, postRequestData);
    }, FORM_SELECTION_SYNC_DEBOUNCE_MS);
}

function ensureSentenceBuilderLoader(loaderStateRef, setSentenceLoader) {
    const state = loaderStateRef.current;
    if (state.shown || state.timer) {
        return;
    }
    state.timer = setTimeout(() => {
        state.timer = null;
        state.shown = true;
        setSentenceLoader?.(true);
    }, SENTENCE_BUILDER_LOADER_DELAY_MS);
}

function clearSentenceBuilderLoader(loaderStateRef, setSentenceLoader) {
    const state = loaderStateRef.current;
    if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
    }
    state.shown = false;
    setSentenceLoader?.(false);
}

function isSentenceBuilderQueueIdle(queueRef, processingRef) {
    return (
        !processingRef.current &&
        (!queueRef.current || queueRef.current.length === 0)
    );
}

function clearSentenceBuilderLoaderIfIdle(
    loaderStateRef,
    setSentenceLoader,
    queueRef,
    processingRef
) {
    if (isSentenceBuilderQueueIdle(queueRef, processingRef)) {
        clearSentenceBuilderLoader(loaderStateRef, setSentenceLoader);
    }
}

async function postSentenceBuilderRequest(url, postRequestData) {
    try {
        return await Promise.race([
            postRequest(url, postRequestData, true),
            new Promise((_, reject) => {
                setTimeout(
                    () => reject(new Error('sentence-builder-timeout')),
                    SENTENCE_BUILDER_API_TIMEOUT_MS
                );
            }),
        ]);
    } catch {
        return false;
    }
}

function resolveCheckboxFieldKey(target) {
    if (!target || target.type === 'text') {
        return '';
    }
    const addNewKey = resolveAddNewFieldKey(target);
    if (addNewKey) {
        return addNewKey;
    }
    if (target.type === 'radio') {
        return target.id || target.name || '';
    }
    return target.name || target.id || '';
}

function isDescendantFieldKey(fieldKey, prefix) {
    return Boolean(
        fieldKey &&
        prefix &&
        fieldKey !== prefix &&
        fieldKey.startsWith(`${prefix}_`)
    );
}

function setFormControlChecked(form, key, checked) {
    if (!key) {
        return;
    }
    const byId = document.getElementById(key);
    if (byId && (byId.type === 'checkbox' || byId.type === 'radio')) {
        byId.checked = checked;
        return;
    }
    const el = form?.[key];
    if (!el) {
        return;
    }
    if (el.length > 1) {
        Array.from(el).forEach((node) => {
            if (node.type === 'checkbox' || node.type === 'radio') {
                node.checked = checked;
            }
        });
        return;
    }
    if (el.type === 'checkbox' || el.type === 'radio') {
        el.checked = checked;
    }
}

function hasCheckedDescendant(prefix, form) {
    if (!form || !prefix) {
        return false;
    }
    return Array.from(form.elements).some((el) => {
        if (!el.checked || (el.type !== 'checkbox' && el.type !== 'radio')) {
            return false;
        }
        const key = resolveCheckboxFieldKey(el);
        return isDescendantFieldKey(key, prefix) || isDescendantFieldKey(el.id, prefix);
    });
}

function uncheckDescendantFields(prefix, form) {
    if (!form || !prefix) {
        return;
    }
    Array.from(form.elements).forEach((el) => {
        if (el.type !== 'checkbox' && el.type !== 'radio') {
            return;
        }
        const key = resolveCheckboxFieldKey(el);
        if (isDescendantFieldKey(key, prefix) || isDescendantFieldKey(el.id, prefix)) {
            el.checked = false;
        }
    });
    tmpAllCheckedArray = tmpAllCheckedArray.filter(
        (k) => k !== prefix && !k.startsWith(`${prefix}_`)
    );
}

function Pare({ setStatement, setDurations, setStatusCollapse, setDateOfServices, data, URL, setAllData,selectAllData, viewEdit, setViewEdit, editMode, notes_id, commonData, saveAfterEdit,setpayloadsData,dataChildToParent,setSentenceLoader, setCheckboxLoader, customPracticeMeta = {}, customPracticeMetaRef = null, accordionPreviewEpochRef = null, onFrontJsonPersisted, onFormSelectionChange }) {
    const CheckForm = useRef();
    const params = useParams();
    const userId = params.id;
    const [selected_notes_ID, setSelected_notes_ID] = useState('');
    const [JsonData, setJsonData] = useState({ isLoading: false, data, clickCount: 0 });
    const pareRefs = useRef({});
    pareRefs.current = {
        setStatement,
        setDurations,
        setDateOfServices,
        setAllData,
        setpayloadsData,
        setViewEdit,
        setSentenceLoader,
        setJsonData,
        viewEdit,
        customPracticeMeta,
        customPracticeMetaRef,
        commonData,
        dataChildToParent,
        URL,
        userId,
        notes_id,
        selected_notes_ID,
        setSelected_notes_ID,
        onFrontJsonPersisted,
        onFormSelectionChange,
    };
    const sentenceBuilderQueueRef = useRef([]);
    const isProcessingSentenceQueueRef = useRef(false);
    const drainDebounceRef = useRef(null);
    const drainFnRef = useRef(async () => {});
    const selectionSyncTimerRef = useRef(null);
    const internalAccordionPreviewEpochRef = useRef(0);
    const accordionEpochRef = accordionPreviewEpochRef ?? internalAccordionPreviewEpochRef;
    const sentenceBuilderLoaderStateRef = useRef({ timer: null, shown: false });
    const formStructureRef = useRef('');
    const prevEditModeRef = useRef(editMode);
    const editHydratedForDocRef = useRef(null);
    const initialPreviewDocRef = useRef(null);
    const documentId = viewEdit?.client_document_id ?? 0;
    // var myVar = $('.searchingTextBG');
// console.log("commonData ===================> In Pare",commonData)
    // useEffect(()=>{        
    // },[commonData,dataChildToParent])

    // var checkData  ;
    const checkUncheckParentChild = (e) => {
        const { checked } = e.target;
        const fullName = resolveCheckboxFieldKey(e.target);
        if (!fullName || e.target.type === 'text') {
            return;
        }

        if (checked) {
            let splittedName = fullName.split('_');
            while (splittedName.length > 1) {
                const tmpFullName = splittedName.join('_');
                if (!tmpAllCheckedArray.includes(tmpFullName)) {
                    tmpAllCheckedArray = [...tmpAllCheckedArray, tmpFullName];
                }
                setFormControlChecked(CheckForm.current, tmpFullName, true);
                splittedName.pop();
            }
            return;
        }

        uncheckDescendantFields(fullName, CheckForm.current);

        let splittedName = fullName.split('_');
        while (splittedName.length > 1) {
            splittedName.pop();
            const parentKey = splittedName.join('_');
            if (hasCheckedDescendant(parentKey, CheckForm.current)) {
                continue;
            }
            setFormControlChecked(CheckForm.current, parentKey, false);
            const parentIndex = tmpAllCheckedArray.indexOf(parentKey);
            if (parentIndex > -1) {
                tmpAllCheckedArray.splice(parentIndex, 1);
            }
        }

        const selfIndex = tmpAllCheckedArray.indexOf(fullName);
        if (selfIndex > -1) {
            tmpAllCheckedArray.splice(selfIndex, 1);
        }
    }
    // function updateBgColor(data){
    //         console.log(data)
    //         console.log(checkData)
    // }
    const postData = useCallback(async (highlightData, highlightId, jobId = 0) => {
        const r = pareRefs.current;
        const formElements = CheckForm.current?.elements
            ? Array.from(CheckForm.current.elements)
            : [];
        const newData = normalizeCustomPracticeFrontJson(
            collectFormSelectionFromElements(formElements)
        );
        let resolvedHighlightId = highlightId || '';
        let resolvedHighlightData = highlightData;
        let notesKey = r.selected_notes_ID;

        if (resolvedHighlightId) {
            r.setSelected_notes_ID(resolvedHighlightId.replace(/['"\\]/g, ''));
            notesKey = resolvedHighlightId.replace(/['"\\]/g, '');
        } else {
            resolvedHighlightData = newData[notesKey + '_notes'];
        }

        if (!r.viewEdit?.client_document_id) {
            r.setJsonData((prev) => ({ ...prev, isLoading: false }));
            return;
        }

        const isCustomPractice = isCustomPracticeAccount();
        const postRequestData = buildSentenceBuilderPostData(
            newData,
            r,
            resolvedHighlightId,
            resolvedHighlightData
        );
        ensureSentenceBuilderLoader(
            sentenceBuilderLoaderStateRef,
            r.setSentenceLoader
        );
        const postdata = await postSentenceBuilderRequest(r.URL, postRequestData);
        const isLatestResponse = jobId === accordionEpochRef.current;
        if (isSentenceBuilderApiSuccess(postdata) && isLatestResponse) {
            r.setStatement(postdata.paragraph);
            applySentenceBuilderResponseToParent(r, postdata, isCustomPractice);
            r.onFrontJsonPersisted?.(
                normalizeCustomPracticeFrontJson(postRequestData),
                postRequestData.client_document_id
            );
            schedulePayloadSyncToParent(r, postRequestData, selectionSyncTimerRef);
            r.setAllData(resolvedHighlightData);
            r.setJsonData((prev) => ({
                ...prev,
                clickCount: prev.clickCount < 1 ? 1 : prev.clickCount,
                isLoading: false,
            }));
        } else if (!isSentenceBuilderApiSuccess(postdata)) {
            r.setJsonData((prev) => ({ ...prev, isLoading: false }));
        }
    }, []);

    const scheduleQueueDrain = useCallback(() => {
        if (drainDebounceRef.current) {
            clearTimeout(drainDebounceRef.current);
        }
        drainDebounceRef.current = setTimeout(() => {
            drainDebounceRef.current = null;
            drainFnRef.current();
        }, SENTENCE_BUILDER_DEBOUNCE_MS);
    }, []);

    const drainSentenceBuilderQueue = useCallback(async () => {
        if (isProcessingSentenceQueueRef.current) {
            return;
        }
        isProcessingSentenceQueueRef.current = true;
        ensureSentenceBuilderLoader(
            sentenceBuilderLoaderStateRef,
            pareRefs.current.setSentenceLoader
        );
        try {
            while (sentenceBuilderQueueRef.current.length > 0) {
                const job = sentenceBuilderQueueRef.current.pop();
                sentenceBuilderQueueRef.current.length = 0;
                await postData(job.chData, job.id, job.jobId);
            }
        } finally {
            isProcessingSentenceQueueRef.current = false;
            clearSentenceBuilderLoaderIfIdle(
                sentenceBuilderLoaderStateRef,
                pareRefs.current.setSentenceLoader,
                sentenceBuilderQueueRef,
                isProcessingSentenceQueueRef
            );
            pareRefs.current.setCheckboxLoader?.(false);
            if (sentenceBuilderQueueRef.current.length > 0) {
                scheduleQueueDrain();
            }
        }
    }, [postData, scheduleQueueDrain]);

    drainFnRef.current = drainSentenceBuilderQueue;

    const flushQueueDrain = useCallback(() => {
        if (drainDebounceRef.current) {
            clearTimeout(drainDebounceRef.current);
            drainDebounceRef.current = null;
        }
        drainFnRef.current();
    }, []);

    useEffect(() => {
        return () => {
            if (drainDebounceRef.current) {
                clearTimeout(drainDebounceRef.current);
                drainDebounceRef.current = null;
            }
            if (selectionSyncTimerRef.current) {
                clearTimeout(selectionSyncTimerRef.current);
                selectionSyncTimerRef.current = null;
            }
            sentenceBuilderQueueRef.current = [];
            isProcessingSentenceQueueRef.current = false;
            clearSentenceBuilderLoader(
                sentenceBuilderLoaderStateRef,
                pareRefs.current.setSentenceLoader
            );
            pareRefs.current.setCheckboxLoader?.(false);
        };
    }, []);

    const queueSentenceBuilder = useCallback((chData, id, immediate = false) => {
        const jobId = accordionEpochRef.current + 1;
        accordionEpochRef.current = jobId;
        sentenceBuilderQueueRef.current.push({ chData, id, jobId });
        ensureSentenceBuilderLoader(
            sentenceBuilderLoaderStateRef,
            pareRefs.current.setSentenceLoader
        );
        if (immediate) {
            flushQueueDrain();
        } else {
            scheduleQueueDrain();
        }
    }, [flushQueueDrain, scheduleQueueDrain]);

    React.useEffect(() => {
        if (!documentId || documentId < 1) {
            initialPreviewDocRef.current = null;
            return;
        }
        const hasEditMode =
            editMode &&
            typeof editMode === 'object' &&
            Object.keys(editMode).length > 0;
        if (hasEditMode || initialPreviewDocRef.current === documentId) {
            return;
        }
        initialPreviewDocRef.current = documentId;
        queueSentenceBuilder('', '', true);
    }, [documentId, editMode, queueSentenceBuilder]);

    const runCheckedFunForEvent = useCallback((f) => {
        bumpCheckboxRestoreEpoch();
        let highlightChData = '';
        let highlightId = '';
        if (!saveAfterEdit && f?.target) {
            checkUncheckParentChild(f);
            const target = f.target;
            const rawId =
                (typeof target.id === 'string' && target.id) ||
                (target.nodeType === 1 ? target.getAttribute('id') : '') ||
                '';
            const idForSplit = rawId.startsWith('{') ? '' : rawId;
            highlightId = rawId.startsWith('{') ? '' : rawId;
            if (idForSplit) {
                const SplitData = idForSplit.split(/[.\_]/);
                highlightChData = SplitData[SplitData.length - 1];
            } else if (typeof target.name === 'string' && target.name) {
                const SplitData = target.name.split(/[.\_]/);
                highlightChData = SplitData[SplitData.length - 1];
            }
        }

        if (viewEdit.client_document_id < 1) {
            setJsonData((prev) => ({ ...prev, isLoading: true }));
        }

        queueSentenceBuilder(highlightChData, highlightId);
    }, [saveAfterEdit, viewEdit.client_document_id, queueSentenceBuilder]);

    const checkedFun = useCallback((f) => {
        runCheckedFunForEvent(f);
    }, [runCheckedFunForEvent]);

    const selectEdit = () => {
        tmpAllCheckedArray = []
        tmpAllAddNewData = []
        const restoredEditMode = normalizeCustomPracticeFrontJson(editMode || {})

        setJsonData((prev) => ({ ...prev, clickCount: 1 }))
        setpayloadsData(restoredEditMode)
        for (let i in restoredEditMode) {
            if (i.includes('_addNewArray')) {
                tmpAllAddNewData = { ...tmpAllAddNewData, [i.replace('_addNewArray', '')]: restoredEditMode[i] }
                restoredEditMode[i].forEach((e) => {
                    if (e.checked) {
                        tmpAllCheckedArray.push(e.editModeName)
                    }
                })
            }
            if (CheckForm.current && CheckForm.current[i]) {
                if (i.includes('notes')) {
                    CheckForm.current[i].value = restoredEditMode[i]
                } else {
                    CheckForm.current[i].checked = restoredEditMode[i]
                    if (CheckForm.current[i].checked) {
                        tmpAllCheckedArray.push(i);
                    }
                    if (restoredEditMode[i]) {
                        let collapseId = document.getElementById(i + 'Collapse')
                        if (collapseId) {
                            collapseId.classList += 'show';
                        }
                    }
                }
            } else if (restoredEditMode[i] && !i.includes('notes') && !i.includes('_addNewArray')) {
                const el = document.getElementById(i)
                if (el) {
                    el.checked = true
                    tmpAllCheckedArray.push(i)
                    const collapseId = document.getElementById(i + 'Collapse')
                    if (collapseId) {
                        collapseId.classList.add('show')
                    }
                }
            }
        }
        if (saveAfterEdit) {
            checkedFun()
        }
    }

    const unselectCheck = () => {
        Array.from(CheckForm.current).map((e) => {
            if (e.type == 'text') {
                e.value = ''
            } else if (e.type == 'checkbox' || e.type == 'radio') {
                e.checked = false
            }
        })

        tmpAllCheckedArray = []
        tmpAllAddNewData = []
    }

    // const checkSelectedKeys = (keys, checked) => {

    //     keys.forEach((e, i) => {
    //         CheckForm.current[e].checked = checked;
    //         let collapseId = document.getElementById(e + 'Collapse')
    //         if (collapseId) {
    //             if (checked) {
    //                 collapseId.classList += ' show';
    //             } else {
    //                 collapseId.classList.remove('show');
    //             }
    //         }
    //         if(checked){
    //             tmpAllCheckedArray.push(e);
    //         }else{
    //             tmpAllCheckedArray = tmpAllCheckedArray.filter((f) => f !== e);
    //         }
    //     })
    // }

    const checkSelectedKeys = (keys, checked, options = {}) => {
        if (!checked && options.clearSectionPrefix) {
            clearSectionFormFields(CheckForm.current, options.clearSectionPrefix);
        }
        keys.forEach((e) => {
            setFormControlChecked(CheckForm.current, e, checked);
            const collapseId = document.getElementById(`${e}Collapse`);
            if (collapseId) {
                if (checked) {
                    collapseId.classList.add('show');
                } else {
                    collapseId.classList.remove('show');
                }
            }
            if (checked) {
                if (!tmpAllCheckedArray.includes(e)) {
                    tmpAllCheckedArray.push(e);
                }
            } else {
                tmpAllCheckedArray = tmpAllCheckedArray.filter((f) => f !== e);
            }
        });
        const triggerTarget =
            document.getElementById(keys[0]) ||
            CheckForm.current?.[keys[0]];
        if (triggerTarget) {
            checkedFun({ target: triggerTarget });
        }
    }

    React.useEffect(() => {
        if (documentId !== editHydratedForDocRef.current?.docId) {
            editHydratedForDocRef.current = null;
        }
    }, [documentId]);

    React.useEffect(() => {
        const hasEditMode =
            editMode &&
            typeof editMode === 'object' &&
            Object.keys(editMode).length > 0;

        if (!hasEditMode) {
            if (prevEditModeRef.current) {
                unselectCheck();
            }
            editHydratedForDocRef.current = null;
            prevEditModeRef.current = editMode;
            return;
        }

        const hydrateKey = `${documentId || 0}`;
        if (editHydratedForDocRef.current?.key !== hydrateKey) {
            selectEdit();
            scheduleFrontJsonCheckboxRestore(editMode);
            editHydratedForDocRef.current = { key: hydrateKey, docId: documentId };
        }

        prevEditModeRef.current = editMode;
    }, [editMode, documentId]);

    React.useEffect(() => {
        setJsonData((prev) => ({ ...prev, data }));
    }, [data])

    React.useEffect(() => {
        if (!editMode || typeof editMode !== 'object') {
            formStructureRef.current = '';
            return;
        }
        const signature = JSON.stringify(
            (data || []).map((section) => ({
                key: section?.key,
                childCount: section?.child?.length ?? 0,
            }))
        );
        if (!signature || signature === formStructureRef.current) {
            return;
        }
        formStructureRef.current = signature;
        const timer = setTimeout(() => {
            selectEdit();
            scheduleFrontJsonCheckboxRestore(editMode);
        }, 60);
        return () => clearTimeout(timer);
    }, [editMode, data])


    const contextValue = useMemo(
        () => ({
            checkedFun,
            editMode,
            userId,
            tmpAllAddNewData,
            checkSelectedKeys,
            ...commonData,
            CheckForm,
            setJsonData,
            setCheckboxLoader,
        }),
        [checkedFun, editMode, userId, commonData, checkSelectedKeys]
    );

    return (
        <changeFuns.Provider value={contextValue}>
            <form ref={CheckForm} id="notenest-accordion-form">
                {JsonData.data.map((e, i) => (
                    <PareChild
                        key={e.key || `section-${i}`}
                        data={e}
                        accordionStatus={setStatusCollapse}
                    />
                ))}
            </form>
        </changeFuns.Provider>
    )
}

export default Pare